import { PageHeader } from '@/components'
import {
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { breadcrumbsItems } from './constants'
import { ROUTE_PATH } from '../AppRoutes'
import { styles } from './styles'
import { TabPanel } from '../EditDictionarieVehicle/components'
import { createContext, useMemo, useRef, useState } from 'react'
import { useGetMedComplexByIdQuery } from '@/api'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ConfigurationInfo,
  GeneralInfo,
  LogInfo,
  VerificationInfo,
} from './Tabs'
import { MedComplexStatus } from './components/MedComplexStatus'
import type { MedComplexErrorForms } from './types'
import { CertificateComplete, ConfigComplete, GeneralComplete } from './modals'
import { useModal, useRole } from '@/hooks'
import { ModalType } from '@/store/app'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { SettingsInfo } from './Tabs/SettingsInfo'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MedComplexContext = createContext({} as any)

export const MedComplex = () => {
  const navigate = useNavigate()
  const { showModal } = useModal()
  const { medcomplexId, medcomplexTab } = useParams()

  const { data: medcomplex, refetch } = useGetMedComplexByIdQuery(
    medcomplexId ? medcomplexId : skipToken
  )
  // const isWindowFocused = useWindowFocus()
  // { pollingInterval: 5000, skip: isWindowFocused }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [typingTimer, setTypingTimer] = useState<any | null>(null)
  const callbackRef = useRef<() => void>(() => {})
  const typingListener = (
    inputValue: string,
    typingPause: number,
    callback: () => void
  ) => {
    clearTimeout(typingTimer)
    callbackRef.current = callback
    setTypingTimer(setTimeout(callback, typingPause))
  }

  const [alignment, setAlignment] = useState<string>(medcomplexTab ?? 'general')
  const [errorForms, setErrorForms] = useState<MedComplexErrorForms>({
    generalInfo: 1,
    verificationInfo: 1,
    generalInfoShown: false,
  })
  //const [isCreate, setCreate] = useState<boolean | null>(null)

  const handleChangeTab = (
    _: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    if (newAlignment) {
      if (typingTimer !== null) {
        clearTimeout(typingTimer)
        setTypingTimer(null)
        callbackRef.current()
        callbackRef.current = () => {}
      }
      setAlignment(newAlignment)
      navigate(`/medcomplex/${medcomplexId}/${newAlignment}`, { replace: true })
    }
  }

  const [costyl, setCostyl] = useState<number>(1)

  useMemo(() => {
    if (medcomplex) {
      const generalInfo = [
        medcomplex.modelId,
        //medcomplex.complex_serial_number,
        medcomplex.bluetooth_serial_number,
        medcomplex.date_creation,
        medcomplex.guarantee_period,
        medcomplex.ownerCompany,
        medcomplex.clientCompanies,
      ].every((item) => Boolean(item))
      const verificationInfo = medcomplex?.certificates
        ? medcomplex?.certificates?.length > 0
        : false
      setErrorForms({
        generalInfo: generalInfo ? 0 : 1,
        verificationInfo: verificationInfo ? 0 : 1,
      })
      if (costyl === 0) {
        const generalInfo = [
          medcomplex.modelId,
          //medcomplex.complex_serial_number,
          medcomplex.bluetooth_serial_number,
          medcomplex.date_creation,
          medcomplex.guarantee_period,
          medcomplex.ownerCompany,
          medcomplex.clientCompanies,
        ].every((item) => Boolean(item))
        const verificationInfo = medcomplex?.certificates
          ? medcomplex?.certificates?.length > 0
          : false
        setErrorForms({
          generalInfo: generalInfo ? 0 : 1,
          verificationInfo: verificationInfo ? 0 : 1,
        })
        if (!errorForms.generalInfoShown) {
          setErrorForms((prev) => {
            if (!prev.generalInfoShown && generalInfo) {
              showModal(ModalType.MEDCOMPLEX_COMPLETE_GENERAL)
              return { ...prev, generalInfoShown: true }
            }
            return { ...prev, generalInfoShown: false }
          })
        }
        if (generalInfo) {
          setErrorForms((prev) => ({ ...prev, generalInfoShown: true }))
        }
      } else {
        setCostyl((prev) => prev - 1)
      }
    }
  }, [medcomplex])

  const { getAccess } = useRole()

  return (
    <Box>
      <PageHeader
        title={
          medcomplex?.model?.name
            ? `${medcomplex?.model?.name} (${medcomplex?.bluetooth_serial_number})`
            : 'Новый медкомплекс'
        }
        breadcrumbs={[
          ...breadcrumbsItems,
          {
            title: medcomplex?.model?.name
              ? `${medcomplex?.model?.name} (${medcomplex?.bluetooth_serial_number})`
              : 'Новый медкомплекс',
          },
        ]}
        backUrl={ROUTE_PATH.MEDCOMPLEX}
        status={
          medcomplex ? (
            <MedComplexStatus medcomplexStatus={medcomplex?.status?.slug} />
          ) : null
        }
        buttons={
          <Stack direction={'row'} gap={2}>
            <Button
              variant="contained"
              onClick={() => {
                navigate('/medcomplex')
              }}
            >
              Перейти к списку медкомплексов
            </Button>
          </Stack>
        }
      />
      <Box
        sx={{
          backgroundColor: 'white',
          margin: '16px',
          padding: '16px',
          borderRadius: '4px',
          height: '100%',
        }}
      >
        <ToggleButtonGroup
          sx={styles.toggleButtonsGroup}
          color="primary"
          value={alignment}
          exclusive
          onChange={handleChangeTab}
        >
          <ToggleButton size={'large'} value={'general'}>
            <Stack direction={'row'} gap={'8px'} alignItems={'center'}>
              Основное
              {errorForms?.generalInfo ? (
                <Chip
                  sx={styles.chip}
                  variant={'outlined'}
                  color="error"
                  label={errorForms?.generalInfo}
                />
              ) : null}
            </Stack>
          </ToggleButton>
          {medcomplex && (
            <ToggleButton size={'large'} value={'certificate'}>
              <Stack direction={'row'} gap={'8px'} alignItems={'center'}>
                Поверка
                {errorForms?.verificationInfo ? (
                  <Chip
                    sx={styles.chip}
                    variant={'outlined'}
                    color="warning"
                    label={errorForms?.verificationInfo}
                  />
                ) : null}
              </Stack>
            </ToggleButton>
          )}
          {medcomplex && getAccess('MEDCOMPLEX', ['UPDATE']) ? (
            <ToggleButton size={'large'} value={'logs'}>
              Логи
            </ToggleButton>
          ) : null}
          {medcomplex && getAccess('MEDCOMPLEX', ['UPDATE']) ? (
            <ToggleButton size={'large'} value={'configuration'}>
              Конфигурирование
            </ToggleButton>
          ) : null}
          {medcomplex && getAccess('MEDCOMPLEX', ['UPDATE']) ? (
            <ToggleButton size={'large'} value={'setings'}>
              Настройка
            </ToggleButton>
          ) : null}
        </ToggleButtonGroup>
        <MedComplexContext.Provider
          value={{
            medcomplex,
            refetch,
            errorForms,
            typingListener,
          }}
        >
          <TabPanel id={'general'} value={alignment}>
            <GeneralInfo />
          </TabPanel>
          <TabPanel id={'certificate'} value={alignment}>
            {medcomplex && <VerificationInfo />}
          </TabPanel>
          <TabPanel id={'logs'} value={alignment}>
            {medcomplex && <LogInfo />}
          </TabPanel>
          <TabPanel id={'configuration'} value={alignment}>
            {medcomplex && <ConfigurationInfo />}
          </TabPanel>
          <TabPanel id={'setings'} value={alignment}>
            {medcomplex && <SettingsInfo />}
          </TabPanel>
        </MedComplexContext.Provider>
      </Box>
      <GeneralComplete />
      <CertificateComplete />
      <ConfigComplete />
    </Box>
  )
}
